import React, { createContext, useContext, useEffect, useState } from "react";

// Global "view as device" + UI-scale controls (driven from the nav Eye circle).
// - device/orientation: preview the live site at tablet/mobile widths.
// - uiScale: zooms the whole app (Shift/Ctrl-click the Eye, or PC presets).
const DeviceViewContext = createContext({
    device: "pc", setDevice: () => {}, orientation: "portrait", setOrientation: () => {},
    uiScale: 1, setUiScale: () => {}, bumpScale: () => {}, pcRes: "1440", setPcRes: () => {},
    contentScaleFor: () => 1, setContentScale: () => {}, resetContentScale: () => {},
});
const KEY = "rosterly_device_view";
const OKEY = "rosterly_device_orient";
const RKEY = "rosterly_pc_res";
const AKEY = "rosterly_pc_ratio";
const CKEY = "rosterly_content_scale";   // owner-only per-page + per-device content zoom

// PC resolution presets → app zoom. On a smaller monitor pick a lower res to
// scale the whole UI down so everything fits like it does on a 1440p screen.
export const PC_RES_SCALE = { "4k": 1.15, "1440": 1.0, "1080": 0.82 };
const clampScale = (v) => Math.max(0.6, Math.min(1.6, Math.round(v * 100) / 100));
// AUTO: the monitor's width in OS-scaled pixels vs the 2560-wide (1440p @100%) reference look,
// so every monitor / OS scale setting shows the same layout. Re-checked when the window moves screens.
// Background monitor sizing (never touches the manual Page %): eased toward 100% so a 1080 screen
// looks like a 1440 one (≈87%) instead of shrinking with the pixel count.
const autoScale = () => Math.max(0.85, Math.min(1.2, Math.round(Math.sqrt((window.screen?.width || window.innerWidth) / 2560) * 100) / 100));

export function DeviceViewProvider({ children }) {
    const [device, setDeviceState] = useState("pc");  // device preview removed — always the real device
    const [orientation, setOrientationState] = useState(() => (typeof window === "undefined" ? "portrait" : localStorage.getItem(OKEY) || "portrait"));
    // Applied zoom = automatic monitor factor × the user's manual Page % (default 100%).
    // The app never writes the manual value — only the user's own +/- does.
    const [monitorScale, setMonitorScale] = useState(() => (typeof window === "undefined" ? 1 : autoScale()));
    const [userScale, setUserScaleState] = useState(() => {
        if (typeof window === "undefined") return null;
        const own = Number(localStorage.getItem("rosterly_page_scale_manual"));
        return Number.isFinite(own) && own > 0 ? own : 1;
    });
    const uiScale = userScale;
    const appliedScale = clampScale(userScale * monitorScale);
    const [pcRes, setPcResState] = useState(() => (typeof window === "undefined" ? "auto" : localStorage.getItem(RKEY) || "auto"));
    // Owner-only per-(page, device) content zoom — scales ON-PAGE content only
    // (not the nav bars), like the Stories download/template scaler.
    const [contentScaleMap, setContentScaleMapState] = useState(() => { try { return JSON.parse(localStorage.getItem(CKEY) || "{}"); } catch { return {}; } });
    const contentScaleFor = (path, dev) => { const v = Number(contentScaleMap[`${path}::${dev}`]); return Number.isFinite(v) && v > 0 ? v : 1; };
    const setContentScale = (path, dev, val) => setContentScaleMapState((m) => {
        const next = { ...m, [`${path}::${dev}`]: clampScale(val) };
        try { localStorage.setItem(CKEY, JSON.stringify(next)); } catch {}
        return next;
    });
    const resetContentScale = (path, dev) => setContentScaleMapState((m) => {
        const key = `${path}::${dev}`; if (!(key in m)) return m;
        const next = { ...m }; delete next[key];
        try { localStorage.setItem(CKEY, JSON.stringify(next)); } catch {}
        return next;
    });

    // Desktop NAV BAR size — separate from page-content scale (null = follow page scale).
    const [navUiScale, setNavUiScaleState] = useState(() => { try { const n = Number(localStorage.getItem("rosterly_nav_scale_desktop")); return Number.isFinite(n) && n > 0 ? n : null; } catch { return null; } });
    const setNavUiScale = (v) => { const n = v == null ? null : clampScale(v); setNavUiScaleState(n); try { if (n == null) localStorage.removeItem("rosterly_nav_scale_desktop"); else localStorage.setItem("rosterly_nav_scale_desktop", String(n)); } catch {} };
    const bumpNavScale = (delta) => setNavUiScale(Math.round((navUiScale ?? uiScale) * 10) / 10 + delta);
    const setDevice = (d) => { setDeviceState(d); try { localStorage.setItem(KEY, d); } catch {} };
    const setOrientation = (o) => { setOrientationState(o); try { localStorage.setItem(OKEY, o); } catch {} };
    const setUiScale = (s) => { const v = clampScale(s); setUserScaleState(v); try { localStorage.setItem("rosterly_page_scale_manual", String(v)); } catch {} };
    // Snap to clean 10% increments (90/100/110…) instead of preset-relative
    // odd values like 95/105.
    const bumpScale = (delta) => setUiScale(Math.round(uiScale * 10) / 10 + delta);
    // Monitor class (account setting) is the single source of the desktop site zoom.
    const setPcRes = (r) => { setPcResState(r); try { localStorage.setItem(RKEY, r); localStorage.removeItem(AKEY); } catch {} setMonitorScale(r === "auto" ? autoScale() : PC_RES_SCALE[r] || 1); };
    useEffect(() => {
        if (pcRes !== "auto" || typeof window === "undefined") return undefined;
        let last = null, mq = null;
        const upd = () => {
            const w = window.screen?.width || 0;
            const key = `${w}@${window.devicePixelRatio}`;
            if (key !== last) { last = key; setMonitorScale(autoScale()); }
        };
        const watchDpr = () => { mq?.removeEventListener?.("change", onDpr); mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`); mq.addEventListener?.("change", onDpr); };
        const onDpr = () => { upd(); watchDpr(); };
        upd(); watchDpr();
        window.addEventListener("resize", upd);
        const t = setInterval(upd, 2000);
        return () => { window.removeEventListener("resize", upd); mq?.removeEventListener?.("change", onDpr); clearInterval(t); };
    }, [pcRes]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { try { localStorage.setItem(KEY, device); } catch {} }, [device]);
    useEffect(() => { try { localStorage.setItem(OKEY, orientation); } catch {} }, [orientation]);
    // Apply the zoom to the whole document (only in normal PC device mode).
    useEffect(() => {
        if (typeof document === "undefined") return;
        const z = device === "pc" && window.innerWidth >= 1024 ? appliedScale : 1;
        document.documentElement.style.zoom = String(z);
        // vh units shrink under html zoom → screen-height bars/navs would lift off the bottom.
        document.documentElement.style.setProperty("--app-zoom", String(z));
        return () => { document.documentElement.style.zoom = "1"; };
    }, [appliedScale, device]);

    return <DeviceViewContext.Provider value={{ device, setDevice, orientation, setOrientation, uiScale, appliedScale, setUiScale, bumpScale, pcRes, setPcRes, contentScaleFor, setContentScale, resetContentScale, navUiScale, setNavUiScale, bumpNavScale }}>{children}</DeviceViewContext.Provider>;
}

export const useDeviceView = () => useContext(DeviceViewContext);
